import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixRestaurantsMenusItemModifierV1ModifiersServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/restaurants-menus-item-modifier',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/restaurants-menus-item-modifier',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/restaurants-menus-item-modifier',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/restaurants-menus-item-modifier',
        destPath: '',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/restaurants-menus-item-modifier',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/restaurants-menus-item-modifier',
        destPath: '',
      },
      {
        srcPath: '/restaurants/menus/v1/bulk/modifiers/delete',
        destPath: '/v1/bulk/modifiers/delete',
      },
      {
        srcPath: '/restaurants/menus/v1/modifiers/count',
        destPath: '/v1/modifiers/count',
      },
      {
        srcPath: '/restaurants/menus/v1/bulk/modifiers/create',
        destPath: '/v1/bulk/modifiers/create',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/restaurants-menus-item-modifier',
        destPath: '',
      },
      {
        srcPath: '/restaurants/menus/v1/bulk/modifiers/delete',
        destPath: '/v1/bulk/modifiers/delete',
      },
      {
        srcPath: '/restaurants/menus/v1/modifiers/count',
        destPath: '/v1/modifiers/count',
      },
      {
        srcPath: '/restaurants/menus/v1/bulk/modifiers/create',
        destPath: '/v1/bulk/modifiers/create',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/restaurants-menus-item-modifier',
        destPath: '',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/restaurants-menus-item-modifier',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/restaurants/item-modifiers',
        destPath: '',
      },
      {
        srcPath: '/restaurants/menus/v1/bulk/modifiers/create',
        destPath: '/v1/bulk/modifiers/create',
      },
      {
        srcPath: '/restaurants/menus/v1/bulk/modifiers/update',
        destPath: '/v1/bulk/modifiers/update',
      },
      {
        srcPath: '/restaurants/menus/v1/bulk/modifiers/delete',
        destPath: '/v1/bulk/modifiers/delete',
      },
      {
        srcPath: '/restaurants/menus/v1/modifiers/count',
        destPath: '/v1/modifiers/count',
      },
    ],
    'apps._base_domain_': [
      {
        srcPath: '/_api/restaurants-menus-item-modifier',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_restaurants_item-modifiers';

/**
 * > **Note:** The Item Modifiers API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Creates an item modifier.
 *
 * To create multiple item modifiers at once, use [Bulk Create Modifiers](https://dev.wix.com/docs/rest/business-solutions/restaurants/menus/item-modifiers/bulk-create-modifiers).
 */
export function createModifier(payload: object): RequestOptionsFactory<any> {
  function __createModifier({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'modifier.createdDate' },
          { path: 'modifier.updatedDate' },
          { path: 'modifier.externalReferenceInfo.lastSyncDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.menus.v1.item_modifier',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.menus_item_modifier.v1.ModifiersService.CreateModifier',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsMenusItemModifierV1ModifiersServiceUrl({
        protoPath: '/v1/modifiers',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'modifier.createdDate' },
              { path: 'modifier.updatedDate' },
              { path: 'modifier.externalReferenceInfo.lastSyncDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createModifier;
}

/**
 * > **Note:** The Item Modifier API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Retrieves an item modifier by ID.
 */
export function getModifier(payload: object): RequestOptionsFactory<any> {
  function __getModifier({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.menus.v1.item_modifier',
      method: 'GET' as any,
      methodFqn:
        'wix.restaurants.menus_item_modifier.v1.ModifiersService.GetModifier',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsMenusItemModifierV1ModifiersServiceUrl({
        protoPath: '/v1/modifiers/{modifierId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'modifier.createdDate' },
              { path: 'modifier.updatedDate' },
              { path: 'modifier.externalReferenceInfo.lastSyncDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getModifier;
}

/**
 * > **Note:** The Item Modifier API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Retrieves a list of up to 500 item modifiers.
 */
export function listModifiers(payload: object): RequestOptionsFactory<any> {
  function __listModifiers({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.menus.v1.item_modifier',
      method: 'GET' as any,
      methodFqn:
        'wix.restaurants.menus_item_modifier.v1.ModifiersService.ListModifiers',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsMenusItemModifierV1ModifiersServiceUrl({
        protoPath: '/v1/modifiers',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'modifiers.createdDate' },
              { path: 'modifiers.updatedDate' },
              { path: 'modifiers.externalReferenceInfo.lastSyncDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listModifiers;
}

/**
 * > **Note:** The Item Modifier API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Updates an item modifier.
 *
 * To update multiple item modifiers at once, use [Bulk Update Item Modifiers](https://dev.wix.com/docs/rest/business-solutions/restaurants/menus/item-modifiers/bulk-update-modifiers).
 *
 * Each time an item modifier is updated, its revision increments by 1. The existing revision must be included when updating an item modifier. This ensures you're working with the latest item modifier information, and it prevents unintended overwrites.
 */
export function updateModifier(payload: object): RequestOptionsFactory<any> {
  function __updateModifier({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'mask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'modifier.createdDate' },
          { path: 'modifier.updatedDate' },
          { path: 'modifier.externalReferenceInfo.lastSyncDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.menus.v1.item_modifier',
      method: 'PATCH' as any,
      methodFqn:
        'wix.restaurants.menus_item_modifier.v1.ModifiersService.UpdateModifier',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsMenusItemModifierV1ModifiersServiceUrl({
        protoPath: '/v1/modifiers/{modifier.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'modifier.createdDate' },
              { path: 'modifier.updatedDate' },
              { path: 'modifier.externalReferenceInfo.lastSyncDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateModifier;
}

/**
 * > **Note:** The Item Modifier API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Creates multiple item modifiers.
 */
export function bulkCreateModifiers(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkCreateModifiers({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'modifiers.createdDate' },
          { path: 'modifiers.updatedDate' },
          { path: 'modifiers.externalReferenceInfo.lastSyncDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.menus.v1.item_modifier',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.menus_item_modifier.v1.ModifiersService.BulkCreateModifiers',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsMenusItemModifierV1ModifiersServiceUrl({
        protoPath: '/v1/bulk/modifiers/create',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.modifier.createdDate' },
              { path: 'results.modifier.updatedDate' },
              { path: 'results.modifier.externalReferenceInfo.lastSyncDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkCreateModifiers;
}

/**
 * > **Note:** The Item modifier API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Updates multiple item modifiers at once.
 * Each time an item modifier is updated, its revision increments by 1. The existing revision must be included when updating the item modifier. This ensures you're working with the latest item modifier information, and prevents unintended overwrites.
 */
export function bulkUpdateModifiers(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateModifiers({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'modifiers.mask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'modifiers.modifier.createdDate' },
          { path: 'modifiers.modifier.updatedDate' },
          { path: 'modifiers.modifier.externalReferenceInfo.lastSyncDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.menus.v1.item_modifier',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.menus_item_modifier.v1.ModifiersService.BulkUpdateModifiers',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsMenusItemModifierV1ModifiersServiceUrl({
        protoPath: '/v1/bulk/modifiers/update',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.modifier.createdDate' },
              { path: 'results.modifier.updatedDate' },
              { path: 'results.modifier.externalReferenceInfo.lastSyncDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkUpdateModifiers;
}

/**
 * > **Note:** The Item modifier API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 * Deletes an item modifier.
 */
export function deleteModifier(payload: object): RequestOptionsFactory<any> {
  function __deleteModifier({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.menus.v1.item_modifier',
      method: 'DELETE' as any,
      methodFqn:
        'wix.restaurants.menus_item_modifier.v1.ModifiersService.DeleteModifier',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsMenusItemModifierV1ModifiersServiceUrl({
        protoPath: '/v1/modifiers/{modifierId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteModifier;
}

/**
 * Creates a query to retrieve a list of item modifiers.
 *
 * The `queryModifiers()` function builds a query to retrieve a list of item modifiers and returns a `ModifiersQueryBuilder` object.
 *
 * The returned object contains the query definition, which is used to run the query using the [`find()`](/item-modifiers/modifiers-query-builder/find) function.
 *
 * You can refine the query by chaining `ModifiersQueryBuilder` functions onto the query. `ModifiersQueryBuilder` functions enable you to filter, sort, and control the results that `queryModifiers()` returns.
 *
 * `queryModifiers()` runs with the following `ModifiersQueryBuilder` defaults, which you can override:
 *
 * * [`limit(500)`](/item-modifiers/modifiers-query-builder/limit)
 *
 * The following `ModifiersQueryBuilder` functions are supported for `queryModifiers()`. For a full description of the item modifier object, see the object returned for the [`items`](/item-modifiers/modifiers-query-result/items) property in `ModifiersQueryResult`.
 */
export function queryModifiers(payload: object): RequestOptionsFactory<any> {
  function __queryModifiers({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.menus.v1.item_modifier',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.menus_item_modifier.v1.ModifiersService.QueryModifiers',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsMenusItemModifierV1ModifiersServiceUrl({
        protoPath: '/v1/modifiers/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'modifiers.createdDate' },
              { path: 'modifiers.updatedDate' },
              { path: 'modifiers.externalReferenceInfo.lastSyncDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryModifiers;
}

/**
 * > **Note:** The Item API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Retrieves the number of modifiers that match a specified filter.
 *
 * If a filter isn't passed in the request, the endpoint returns the count of all modifiers.
 */
export function countModifiers(payload: object): RequestOptionsFactory<any> {
  function __countModifiers({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.menus.v1.item_modifier',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.menus_item_modifier.v1.ModifiersService.CountModifiers',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsMenusItemModifierV1ModifiersServiceUrl({
        protoPath: '/v1/modifiers/count',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __countModifiers;
}

/**
 * > **Note:** The Item Modifier API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Deletes multiple item Modifiers at once.
 */
export function bulkDeleteModifiers(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkDeleteModifiers({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.menus.v1.item_modifier',
      method: 'DELETE' as any,
      methodFqn:
        'wix.restaurants.menus_item_modifier.v1.ModifiersService.BulkDeleteModifiers',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsMenusItemModifierV1ModifiersServiceUrl({
        protoPath: '/v1/bulk/modifiers/delete',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __bulkDeleteModifiers;
}
